import React, { useRef, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, Sparkles, CheckCircle2, XCircle, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";

const EXAMPLE = `Cassoulet
---
Quiche lorraine
---
Tarte Tatin aux pommes
---
Bœuf bourguignon — épaule de bœuf, vin rouge, lardons, oignons grelots, carottes, champignons, bouquet garni. Faire mariner 12h. Cuire 3h.`;

export default function AdminBatchAdapt() {
  const [mode, setMode] = useState("text"); // 'text' | 'file'
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [generateImage, setGenerateImage] = useState(true);
  const [publish, setPublish] = useState(true);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const fileRef = useRef();

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "text" && !text.trim()) {
      toast.error("Collez au moins une recette");
      return;
    }
    if (mode === "file" && !file) {
      toast.error("Sélectionnez un fichier");
      return;
    }

    setLoading(true);
    setResults(null);
    const t0 = Date.now();
    try {
      const fd = new FormData();

      // Standard (text / file) → Claude adaptation
      if (mode === "text") fd.append("text", text);
      if (mode === "file") fd.append("file", file);
      fd.append("generate_image", String(generateImage));
      fd.append("publish", String(publish));

      const res = await fetch(`${API}/admin/recipes/batch-adapt`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setResults({ ...data, duration_s: ((Date.now() - t0) / 1000).toFixed(1) });
      if (data.created?.length) toast.success(`${data.created.length} recette(s) adaptée(s)`);
      if (data.errors?.length) toast.error(`${data.errors.length} échec(s)`);
    } catch (err) {
      toast.error(err.message || "Erreur lors de l'adaptation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-batch-page">
      <p className="overline mb-2">Claude Sonnet 4.6 · Adaptation diabétique</p>
      <h1 className="font-serif text-4xl mb-2">Adapter un batch de recettes</h1>
      <p className="text-[hsl(var(--text-secondary))] mb-8 max-w-3xl">
        Fournissez vos recettes traditionnelles (texte brut ou fichier). Claude les adapte une à une en IG bas,
        en conservant l'identité du plat. Vous pouvez n'indiquer que les titres : Claude créera la recette adaptée.
        Maximum <strong>20 recettes</strong> par lot.
      </p>

      <form onSubmit={submit} className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-6 mb-8" data-testid="batch-form">
        {/* MODE TABS */}
        <div className="flex border-b border-[hsl(var(--border-light))] mb-6">
          <button
            type="button"
            onClick={() => setMode("text")}
            data-testid="batch-mode-text"
            className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors ${
              mode === "text"
                ? "border-b-2 border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))]"
                : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
            }`}
          >
            <FileText className="w-4 h-4" strokeWidth={1.5} /> Coller du texte
          </button>
          <button
            type="button"
            onClick={() => setMode("file")}
            data-testid="batch-mode-file"
            className={`flex items-center gap-2 px-5 py-3 text-sm transition-colors ${
              mode === "file"
                ? "border-b-2 border-[hsl(var(--brand-primary))] text-[hsl(var(--brand-primary))]"
                : "text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text-primary))]"
            }`}
          >
            <Upload className="w-4 h-4" strokeWidth={1.5} /> Upload de fichier
          </button>
        </div>

        {mode === "text" ? (
          <div>
            <label className="overline block mb-2">Recettes — séparées par <code>---</code></label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="batch-text-input"
              rows={14}
              placeholder={EXAMPLE}
              className="w-full text-sm font-mono px-3 py-3 bg-[hsl(var(--bg-subtle))]/40 border border-[hsl(var(--border-light))] rounded-sm focus:outline-none focus:border-[hsl(var(--brand-primary))]"
            />
            <p className="mt-2 text-xs text-[hsl(var(--text-muted))]">
              Astuce : un seul titre (ex: "Cassoulet") suffit — Claude crée la recette complète adaptée.
            </p>
          </div>
        ) : (
          <div>
            <label className="overline block mb-2">Fichier (.txt, .md, .csv)</label>
            <input
              type="file"
              accept=".txt,.md,.csv,.text"
              ref={fileRef}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="batch-file-input"
              className="block w-full text-sm text-[hsl(var(--text-secondary))] file:mr-4 file:py-2 file:px-4 file:rounded-sm file:border-0 file:bg-[hsl(var(--brand-primary))] file:text-white file:cursor-pointer hover:file:bg-[hsl(var(--brand-primary-hover))]"
            />
            {file && (
              <p className="mt-2 text-xs text-[hsl(var(--text-muted))]" data-testid="batch-file-name">
                {file.name} — {(file.size / 1024).toFixed(1)} Ko
              </p>
            )}
            <p className="mt-2 text-xs text-[hsl(var(--text-muted))]">
              Format attendu : recettes séparées par <code>---</code> dans le fichier.
            </p>
          </div>
        )}

        {/* OPTIONS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 pt-6 border-t border-[hsl(var(--border-light))]">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={generateImage}
              onChange={(e) => setGenerateImage(e.target.checked)}
              data-testid="batch-image-checkbox"
              className="mt-1 accent-[hsl(var(--brand-primary))]"
            />
            <span>
              <span className="block text-sm font-medium">Générer une image (Nano Banana)</span>
              <span className="block text-xs text-[hsl(var(--text-muted))]">+ ~15s par recette</span>
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={publish}
              onChange={(e) => setPublish(e.target.checked)}
              data-testid="batch-publish-checkbox"
              className="mt-1 accent-[hsl(var(--brand-primary))]"
            />
            <span>
              <span className="block text-sm font-medium">Publier directement</span>
              <span className="block text-xs text-[hsl(var(--text-muted))]">décochez pour mettre en brouillon</span>
            </span>
          </label>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="submit"
            disabled={loading}
            data-testid="batch-submit-btn"
            className="btn-primary disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" strokeWidth={1.5} />
            {loading ? "Adaptation en cours…" : "Lancer l'adaptation"}
          </button>
          {loading && (
            <p className="text-xs text-[hsl(var(--text-muted))]">
              ~20-40s par recette · ne fermez pas cette page
            </p>
          )}
        </div>
      </form>

      {results && (
        <div className="bg-white border border-[hsl(var(--border-light))] rounded-sm p-6" data-testid="batch-results">
          <div className="flex items-baseline justify-between mb-6">
            <h2 className="font-serif text-2xl">Résultat du lot</h2>
            <p className="text-xs text-[hsl(var(--text-muted))]">{results.duration_s}s · {results.received} reçue(s)</p>
          </div>

          {results.created?.length > 0 && (
            <div className="mb-6">
              <p className="overline mb-3 text-[hsl(var(--gi-low))]">
                <CheckCircle2 className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                Succès — {results.created.length}
              </p>
              <ul className="space-y-2">
                {results.created.map((r) => (
                  <li key={r.id} className="flex items-center justify-between border-b border-[hsl(var(--border-light))] pb-2" data-testid={`batch-result-success-${r.index}`}>
                    <div>
                      <span className="font-medium">{r.title}</span>
                      <span className="text-xs text-[hsl(var(--text-muted))] ml-2">{r.category} · IG {r.gi}</span>
                    </div>
                    <Link to={`/recettes/${r.slug}`} target="_blank" className="text-xs text-[hsl(var(--brand-primary))] hover:underline flex items-center gap-1">
                      Voir <LinkIcon className="w-3 h-3" strokeWidth={1.5} />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {results.errors?.length > 0 && (
            <div>
              <p className="overline mb-3 text-[hsl(var(--brand-primary))]">
                <XCircle className="inline w-3.5 h-3.5 mr-1" strokeWidth={1.5} />
                Échecs — {results.errors.length}
              </p>
              <ul className="space-y-2 text-sm">
                {results.errors.map((e, i) => (
                  <li key={i} className="border-l-2 border-[hsl(var(--brand-primary))] pl-3 py-1" data-testid={`batch-result-error-${e.index}`}>
                    <p className="text-xs text-[hsl(var(--text-muted))] truncate">{e.input_preview}</p>
                    <p className="text-xs text-[hsl(var(--brand-primary))]">{e.error}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
